/**
 * Who is signed in, and what they are allowed to see.
 *
 * Kept in React context rather than read from the query cache directly,
 * because the router's guards need it synchronously in `beforeLoad` — before
 * any component has rendered and therefore before any hook has run.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  api,
  beginImpersonation,
  clearSession,
  hasStoredSession,
  login as apiLogin,
  onSessionEnded,
  refreshSession,
  register as apiRegister,
} from "../api/client";
import type { Membership, User } from "../api/types";
import type { SignupAttribution } from "./attribution";

export interface Session {
  /** Undefined while the stored refresh token is still being redeemed. */
  user: User | null;
  memberships: Membership[];
  ready: boolean;
  /** Set while acting as another account. Drives the warning banner. */
  impersonating: string | null;

  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, attribution?: SignupAttribution): Promise<void>;
  signOut(): void;
  impersonate(token: string, email: string): Promise<void>;
  reload(): Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [ready, setReady] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const load = useCallback(async () => {
    const me = await api.get<User>("/api/v1/users/me");
    setUser(me);
    setMemberships(await api.get<Membership[]>("/api/v1/orgs"));
  }, []);

  // Restore a session from the stored refresh token on first load. Until this
  // settles the router must not decide anything — see `ready`.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (hasStoredSession() && (await refreshSession())) {
        try {
          if (!cancelled) await load();
        } catch {
          // The token redeemed but the profile call failed. Treat it as signed
          // out rather than leaving a half-session that fails on every screen.
          clearSession();
        }
      }
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [load]);

  // The client signals here when a refresh finally fails, which is the only
  // way a session ends without the user asking.
  useEffect(
    () =>
      onSessionEnded(() => {
        setUser(null);
        setMemberships([]);
        setImpersonating(null);
      }),
    []
  );

  const value = useMemo<Session>(
    () => ({
      user,
      memberships,
      ready,
      impersonating,

      async signIn(email, password) {
        setUser(await apiLogin(email, password));
        setMemberships(await api.get<Membership[]>("/api/v1/orgs"));
      },

      async signUp(email, password, attribution) {
        setUser(await apiRegister(email, password, attribution));
        setMemberships(await api.get<Membership[]>("/api/v1/orgs"));
      },

      signOut() {
        // Best-effort: revoking server-side is what actually ends the session,
        // but a failed call must not leave the user apparently signed in.
        void api.post("/api/v1/auth/jwt/logout").catch(() => undefined);
        clearSession();
        setUser(null);
        setMemberships([]);
        setImpersonating(null);
      },

      async impersonate(token, email) {
        beginImpersonation(token);
        setImpersonating(email);
        await load();
      },

      reload: load,
    }),
    [user, memberships, ready, impersonating, load]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside <SessionProvider>");
  return session;
}

/**
 * The organization whose data the dashboard is showing.
 *
 * Every account gets a personal organization at registration, so there is
 * always at least one. Multi-org accounts pick between them; the rest never
 * see a chooser.
 */
export function useCurrentOrganization(): Membership | null {
  const { memberships } = useSession();
  return memberships[0] ?? null;
}
